import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'query-string';
import { RootState } from 'modules/store';
import Errors from 'modules/shared/error/errors';
import type { PaginatedResponse } from 'modules/avs/avs.types';
import { downloadCSVFile } from 'utils/dowloadCSVFile';
import {
  CorporateVerificationBulkDetails,
  CorporateVerificationDetails,
  CorporateVerificationRecord,
} from 'modules/corporateVerification/corporateVerification.types';
import CorporateVerificationService from 'modules/corporateVerification/corporateVerification.service';

function getPage(): number {
  const queryParams = QueryString.parse(window.location.search);
  return Number(queryParams.page || 1);
}

interface RejectValue {
  message: string;
  status?: number;
  errorCode?: string;
}

export const uploadCorporateBulkVerification = createAsyncThunk<
  CorporateVerificationBulkDetails,
  {
    appToken: string;
    data: File;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'corporateVerification/bulk-verification',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await CorporateVerificationService.uploadBulkVerification({
        appToken,
        appEnvironment,
        data,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    } finally {
      const page = getPage();
      dispatch(getCorporateVerificationRecords({ appToken, page }));
    }
  },
);

export const getCorporateVerificationRecords = createAsyncThunk<
  PaginatedResponse<CorporateVerificationRecord>,
  {
    appToken: string;
    page: number;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'corporateVerification/get-verification-records',
  async ({ appToken, page }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await CorporateVerificationService.getVerificationRecords({
        appToken,
        appEnvironment,
        page,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getCorporateBulkVerificationResult = createAsyncThunk<
  CorporateVerificationBulkDetails,
  {
    appToken: string;
    requestId: string;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'corporateVerification/get-bulk-verification-result',
  async ({ appToken, requestId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await CorporateVerificationService.getBulkVerificationResult({
        appToken,
        requestId,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getCorporateBulkVerificationResultData = createAsyncThunk<
  PaginatedResponse<CorporateVerificationDetails>,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    page?: number;
  },
  {
    state: RootState;
    rejectValue: RejectValue;
  }
>(
  'corporateVerification/get-bulk-verification-result-data',
  async (
    { appToken, requestId, isCsvFile, page },
    { getState, rejectWithValue },
  ) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await CorporateVerificationService.getBulkVerificationResultData({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
        page,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `corporate_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

